import { Bank, BankAccountType, DisplayValue, DisplayValueDisabled, FileAccordionData, IdentificationType, MaritalStatus, NavigationOption, PayCycle, TaskRequestType, TaskRequestTypeDescriptions, Title } from "common";
import { CurrencyMaskConfig } from "ng2-currency-mask";

export const WeeklyPayDateOptions: DisplayValue<number>[] = [
    {
        value: 1,
        display: 'Monday',
    },
    {
        value: 2,
        display: 'Tuesday',
    },
    {
        value: 3,
        display: 'Wednesday',
    },
    {
        value: 4,
        display: 'Thursday',
    },
    {
        value: 5,
        display: 'Friday',
    },
    {
        value: 6,
        display: 'Saturday',
    },
    // { // Sundays aren't options.
    //     value: 7,
    //     display: 'Sunday',
    // }
];

export const PayCyclesOptions: DisplayValue<PayCycle>[] = [
    {
        value: PayCycle.Monthly,
        display: 'Monthly',
    },
    {
        value: PayCycle.Weekly,
        display: 'Weekly',
    },
    {
        value: PayCycle.Fortnightly,
        display: 'Fortnightly',
    }
];

export const MonthlyPayDateOptions: DisplayValue<number>[] = Array.from({ length: 31 }, (_, i) => ({
    value: (i + 1),
    display: (i + 1).toString(),
})).concat([
    // { value: -2, display: 'Second Last Day of Month' }, // Not used anymore.
    { value: -1, display: 'Last Day of Month' }
]);

export const IdTypeOptions: DisplayValue<IdentificationType>[] = [
    {
        value: IdentificationType.RSAID,
        display: 'RSA ID',
    },
    {
        value: IdentificationType.Passport,
        display: 'Passport',
    },
    {
        value: IdentificationType.Other,
        display: 'Other',
    }
];

export const TitleOptions: DisplayValue<Title>[] = [
    {
        value: Title.Mr,
        display: 'Mr',
    },
    {
        value: Title.Mrs,
        display: 'Mrs',
    },
    {
        value: Title.Miss,
        display: 'Miss',
    },
    {
        value: Title.Master,
        display: 'Master',
    },
    {
        value: Title.Prof,
        display: 'Professor',
    },
    {
        value: Title.Dr,
        display: 'Dr',
    },
    {
        value: Title.Ds,
        display: 'Ds',
    },
    {
        value: Title.Rabbi,
        display: 'Rabbi',
    },
    {
        value: Title.TheHon,
        display: 'The Honourable',
    },
    {
        value: Title.Ms,
        display: 'Ms',
    },
    {
        value: Title.Rev,
        display: 'Reverend',
    },
    {
        value: Title.Sgt,
        display: 'Sargent',
    },
    {
        value: Title.Sister,
        display: 'Sister',
    }
];

// Employment status lookup (mirrors the legacy optimal-client values).
// Release requires "Verified" (value 2) for Optifin debtors.
export const EmploymentStatuses: DisplayValue<number>[] = [
    {
        value: 0,
        display: 'None',
    },
    {
        value: 1,
        display: 'Pending',
    },
    {
        value: 2,
        display: 'Verified',
    }
];

export const MaritalStatuses: DisplayValue<MaritalStatus>[] = [
    {
        value: MaritalStatus.Unknown,
        display: 'Unknown',
    },
    {
        value: MaritalStatus.Single,
        display: 'Single',
    },
    {
        value: MaritalStatus.Married,
        display: 'Married',
    },
    {
        value: MaritalStatus.Divorced,
        display: 'Divorced',
    }
];

export const CaptureDialogFilesAccordionSetup: FileAccordionData[] = [
    // ID, Passport - Mandatory field. Can save as PDF or JPEG. Single file only.
    {
        accept: '.pdf,.jpeg,.jpg,.png',
        buttonTooltip: 'ID Document (RSA ID, Passport etc.) in PDF, JPEG or PNG',
        description: 'RSA ID, Passport etc.',
        formControlNameId: 'photoIDBookId',
        formControlNameResource: 'photoIDBook',
        isDisabled: false,
        isRequired: true,
        multiple: false,
        title: 'ID Document',
        fileMapKey: 'photoIDBook'
    },

    // Patient Card - Mandatory field.Can save as PDF or JPEG. Single file only.
    {
        accept: '.pdf,.jpeg,.jpg,.png',
        buttonTooltip: 'Patient Card in PDF, JPEG or PNG',
        description: 'Photo or scan of patient card.',
        formControlNameId: 'photoPatientCardId',
        formControlNameResource: 'photoPatientCard',
        isDisabled: false,
        isRequired: true,
        multiple: false,
        title: 'Patient Card or Invoice',
        fileMapKey: 'photoPatientCard'
    },

    // Proof of Income - Mandatory field. Can save as a PDF or JPEG. Single file only.
    {
        accept: '.pdf,.jpeg,.jpg,.png',
        buttonTooltip: 'Proof of Income (bank statements, pay slips or employer letter) in PDF, JPEG or PNG',
        description: '3 Months Bank Statements/3 Months Pay Slip/Employer Letter',
        formControlNameId: 'photoPayslipId',
        formControlNameResource: 'photoPayslip',
        isDisabled: false,
        isRequired: true,
        multiple: false,
        title: 'Proof of Income',
        fileMapKey: 'photoPayslip',
        // The slot holds a single file, so extra statements or pay slips go under Miscellaneous.
        note: 'Only one file can be uploaded here. If you have more than one file (e.g. 3 months of bank statements or pay slips), upload the rest under Miscellaneous.'
    },

    // Lab Order - Voluntary Field.Can save as a PDF or JPEG. Single file only.
    {
        accept: '.pdf,.jpeg,.jpg,.png',
        buttonTooltip: 'Lab Order (if applicable) in PDF, JPEG or PNG',
        description: 'If applicable',
        formControlNameId: 'photoLabOrderId',
        formControlNameResource: 'photoLabOrder',
        isDisabled: false,
        isRequired: false,
        multiple: false,
        title: 'Lab Order',
        fileMapKey: 'photoLabOrder'
    },

    // Miscellaneous - Voluntary Field.Can save as a PDF or JPEG. Allows multiples.
    {
        accept: '.pdf,.jpeg,.jpg,.png',
        buttonTooltip: 'Miscellaneous files in PDF, JPEG or PNG',
        description: 'Other documents',
        formControlNameId: 'vendorApplicationCustomerResources',
        formControlNameResource: 'vendorApplicationCustomerResources',
        isDisabled: false,
        isRequired: false,
        multiple: true,
        title: 'Miscellaneous',
        fileMapKey: 'vendorApplicationCustomerResources'
    }
];

/**
 * Maximum base loan amount (before fees) for vendor loans (Specsfin / Dentifin).
 * Mirrored on the backend in VendorApplication Submit.
 */
export const VENDOR_MAX_LOAN_AMOUNT = 15000;

export const AllTaskRequestTypes: DisplayValue<TaskRequestType>[] = [
    {
        display: TaskRequestTypeDescriptions[TaskRequestType.cancellation_request],
        value: TaskRequestType.cancellation_request
    },
    {
        display: TaskRequestTypeDescriptions[TaskRequestType.customer_request_update_details],
        value: TaskRequestType.customer_request_update_details
    },
    {
        display: TaskRequestTypeDescriptions[TaskRequestType.change_bank_details],
        value: TaskRequestType.change_bank_details
    },
    {
        display: TaskRequestTypeDescriptions[TaskRequestType.customer_request_amend_loan_amount],
        value: TaskRequestType.customer_request_amend_loan_amount
    },
    {
        display: TaskRequestTypeDescriptions[TaskRequestType.change_of_debit_order_date],
        value: TaskRequestType.change_of_debit_order_date
    },
    {
        display: TaskRequestTypeDescriptions[TaskRequestType.customer_request_change_debit_order_amount],
        value: TaskRequestType.customer_request_change_debit_order_amount
    },
    {
        display: TaskRequestTypeDescriptions[TaskRequestType.special_debit_order_request],
        value: TaskRequestType.special_debit_order_request
    },
    {
        display: TaskRequestTypeDescriptions[TaskRequestType.customer_request_change_start_date],
        value: TaskRequestType.customer_request_change_start_date
    },
    {
        display: TaskRequestTypeDescriptions[TaskRequestType.customer_request_general_queries],
        value: TaskRequestType.customer_request_general_queries
    }
];

export const BankAccountTypesOptions: DisplayValueDisabled<BankAccountType>[] = [
    {
        display: 'None',
        value: BankAccountType.None,
        isDisabled: true,
    },
    {
        display: 'Current',
        value: BankAccountType.Current
    },
    {
        display: 'Savings',
        value: BankAccountType.Savings
    },
];

export const StaticBankList: Array<Bank> = [
    {
        "id": 2,
        "name": "ABSA Bank",
        "code": "632005"
    },
    {
        "id": 3,
        "name": "African Bank",
        "code": "430000"
    },
    {
        "id": 6,
        "name": "Bidvest Bank",
        "code": "462005"
    },
    {
        "id": 8,
        "name": "Capitec Bank",
        "code": "470010"
    },
    {
        "id": 21630,
        "name": "Discovery Bank",
        "code": "679000"
    },
    {
        "id": 11,
        "name": "First National Bank",
        "code": "250655"
    },
    {
        "id": 14,
        "name": "Investec Bank",
        "code": "580105"
    },
    {
        "id": 22717,
        "name": "Ithala bank",
        "code": "757826"
    },
    {
        "id": 17,
        "name": "Mercantile Bank",
        "code": "450905"
    },
    {
        "id": 20,
        "name": "Nedcor",
        "code": "198765"
    },
    {
        "id": 25,
        "name": "PostBank div of SA Post Office",
        "code": "460005"
    },
    {
        "id": 6161,
        "name": "Sasfin",
        "code": "683000"
    },
    {
        "id": 27,
        "name": "South African Bank of Athens",
        "code": "410700"
    },
    {
        "id": 29,
        "name": "Standard Bank",
        "code": "051001"
    },
    {
        "id": 20359,
        "name": "Tyme Bank",
        "code": "678910"
    },
    {
        "id": 31,
        "name": "UBANK",
        "code": "431010"
    }
];

export const OptometristMenuOptions: NavigationOption[] = [
    {
        // title: 'Create a New Application',
        title: 'New Application',
        icon: 'add',
        isCurrentNav: false,
        path: '/applications/saved',
        queryParam: { new: 'true' },
        description: 'Create a new application',
    },
    { // TODO check if relocation to here is OK.
        title: 'Active Applications',
        icon: 'done',
        isCurrentNav: false,
        headerClass: 'hide-at-840',
        path: '/applications/active',
        description: 'View active applications',
    },
    {
        title: 'Applications',
        icon: 'apps',
        isCurrentNav: false,
        path: '/applications',
        description: 'View all applications',
        isHeader: true,
        children: [
            {
                title: 'Saved Applications',
                icon: 'save',
                isCurrentNav: false,
                path: '/applications/saved',
                description: 'View saved applications',
            },
            {
                title: 'Submitted Applications',
                icon: 'send',
                isCurrentNav: false,
                path: '/applications/submitted',
                description: 'View submitted applications',
            },
            {
                title: 'Pre-Approved Applications',
                icon: 'playlist_add_check',
                isCurrentNav: false,
                path: '/applications/pre-approved',
                description: 'View pre-approved applications',
            },
            {
                title: 'Active Applications',
                icon: 'done',
                isCurrentNav: false,
                path: '/applications/active',
                description: 'View active applications',
            },
            {
                title: 'Cancelled or Declined Applications',
                icon: 'cancel',
                isCurrentNav: false,
                path: '/applications/cancelled-declined',
                description: 'View cancelled or declined applications',
            },
            {
                title: 'Closed Applications',
                icon: 'close',
                isCurrentNav: false,
                path: '/applications/closed',
                description: 'View closed applications',
            },
            {
                title: 'Legal, Deceased or Administration Applications',
                icon: 'warning',
                isCurrentNav: false,
                path: '/applications/legal-deceased-admin',
                description: 'View legal, deceased or administration applications',
            },
        ],
    },
    {
        title: 'Bookings',
        icon: 'event_note',
        isCurrentNav: false,
        path: '/bookings',
        description: 'Manage booking requests',
        isHeader: true,
        badgeCount: 0,
        children: [
            {
                title: 'Requests',
                icon: 'schedule',
                isCurrentNav: false,
                path: '/bookings/requests',
                description: 'New booking requests',
            },
            {
                title: 'Booked',
                icon: 'event_available',
                isCurrentNav: false,
                path: '/bookings/booked',
                description: 'Scheduled appointments',
            },
            {
                title: 'Closed',
                icon: 'check_circle',
                isCurrentNav: false,
                path: '/bookings/closed',
                description: 'Completed requests',
            },
        ],
    },
    {
        title: 'Payouts',
        icon: 'payment',
        isCurrentNav: false,
        path: '/payouts',
        description: 'View payouts',
        isHeader: true,
        children: [
            {
                title: 'Unpaid',
                icon: 'money_off',
                isCurrentNav: false,
                path: '/payouts/unpaid',
                description: 'View unpaid payouts',
            },
            {
                title: 'Paid',
                icon: 'attach_money',
                isCurrentNav: false,
                path: '/payouts/paid',
                description: 'View paid payouts',
            },
            {
                title: 'Payout Reports',
                icon: 'description',
                isCurrentNav: false,
                path: '/payouts/reports',
                description: 'View payout reports',
            },
        ],
    },
    {
        title: 'Admin',
        icon: 'admin_panel_settings',
        isCurrentNav: false,
        path: '/admin',
        description: 'Admin settings',
        isHeader: true,
        children: [
            {
                title: 'Practice Details',
                icon: 'business',
                isCurrentNav: false,
                path: '/admin/practice-details',
                description: 'View practice details',
            },
            {
                title: 'Practitioner Signature',
                icon: 'edit',
                isCurrentNav: false,
                path: '/admin/practitioner-signature',
                description: 'View practitioner signature',
            },
            {
                title: 'New Staff Form',
                icon: 'person_add',
                isCurrentNav: false,
                path: '/admin/new-staff-form',
                description: 'View new staff form',
            },
            {
                title: 'Loan Amendment Form',
                icon: 'edit_document',
                isCurrentNav: false,
                path: '/admin/loan-amendment',
                description: 'Authorise changes to a loan',
            },
            {
                title: 'Customer Information Form',
                icon: 'info',
                isCurrentNav: false,
                path: '/admin/customer-information-form',
                description: 'Download customer information form',
            },
            {
                title: 'Training',
                icon: 'school',
                isCurrentNav: false,
                path: '/admin/training',
                description: 'View training resources and videos',
            }
        ],
    },
];

export const CustomerZoneMenuOptions: NavigationOption[] = [
    {
        title: 'Applications',
        icon: 'description',
        isCurrentNav: false,
        path: '/applications',
        description: 'View and manage your applications',
    },
    {
        title: 'Personal Details',
        icon: 'person',
        isCurrentNav: false,
        path: '/personal-details',
        description: 'View your personal details',
    },
    // {
    //   title: 'Contact',
    //   icon: 'contact_mail',
    //   isCurrentNav: false,
    //   path: '/contact',
    //   description: 'View your contact records',
    // }
];

export const CustomCurrencyMaskConfig: CurrencyMaskConfig = {
    align: "left",
    allowNegative: false,
    decimal: ".",
    precision: 2,
    prefix: "R ",
    suffix: "",
    thousands: ","
};